/**
 * Battle feedback visuals (owner: combat-builder): slash arc, hit spark, guard shimmer and floating
 * damage numbers. Textures are looked up by the inventory ids (`vfx-slash-arc`, `vfx-hit-spark`,
 * `vfx-guard-shimmer`); when the real art is absent a deterministic dev sheet is drawn under
 * `<id>--dev` so the animation keys stay identical and the art drops in without code changes.
 * The shared effects director is asked first (`effects.play(...)`); the local sprite is the
 * battle's own guaranteed feedback while that director is still a stub.
 */
import Phaser from 'phaser';
import { FONT_BODY } from '@/core/config';
import type { IEffectsDirector } from '@/core/types/contracts';
import { ONE_SHOT_NAMES } from '@/effects';
import { battleText } from './text';

export type FxName = 'slash-arc' | 'hit-spark' | 'guard-shimmer';

interface FxSpec { texture: string; frames: number; w: number; h: number; fps: number; draw: (ctx: CanvasRenderingContext2D, n: number, total: number, w: number, h: number) => void }

const SPECS: Record<FxName, FxSpec> = {
  'slash-arc': { texture: 'vfx-slash-arc', frames: 6, w: 64, h: 48, fps: 24, draw: drawSlash },
  'hit-spark': { texture: 'vfx-hit-spark', frames: 5, w: 32, h: 32, fps: 24, draw: drawSpark },
  'guard-shimmer': { texture: 'vfx-guard-shimmer', frames: 4, w: 48, h: 64, fps: 12, draw: drawShimmer },
};

export function fxAnimKey(name: FxName): string { return `combat-fx:${name}`; }

function px(ctx: CanvasRenderingContext2D, x: number, y: number, c: string, s = 1): void { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), s, s); }

/** Sweeping crescent: white core, sand edge, fading to lacquer. */
function drawSlash(ctx: CanvasRenderingContext2D, n: number, total: number, w: number, h: number): void {
  const t = n / Math.max(1, total - 1);
  const cx = w * 0.3, cy = h * 0.5, r = w * 0.55;
  const from = -1.1, to = -1.1 + t * 2.2;
  const cols = t < 0.5 ? ['#fff8e6', '#e2c48c'] : ['#e2c48c', '#b3453a'];
  for (let a = from; a <= to; a += 0.045) {
    const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
    px(ctx, x, y, cols[0] as string, 2);
    px(ctx, x - Math.cos(a) * 3, y - Math.sin(a) * 3, cols[1] as string, 1);
  }
  // trailing motion ticks
  if (t > 0.3) for (let i = 0; i < 3; i++) px(ctx, cx + r + 4 + i * 4, cy - 10 + i * 8, 'rgba(255,255,255,0.7)', 3);
}

/** Expanding eight-ray star that burns from white to lantern orange. */
function drawSpark(ctx: CanvasRenderingContext2D, n: number, total: number, w: number, h: number): void {
  const t = n / Math.max(1, total - 1);
  const cx = w / 2, cy = h / 2;
  const len = 3 + Math.round(t * 11);
  const col = t < 0.35 ? '#fff8e6' : t < 0.7 ? '#ffb347' : '#c2622a';
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + (i % 2 ? 0.2 : 0);
    const start = Math.round(t * 5);
    for (let d = start; d < len; d += 1) px(ctx, cx + Math.cos(a) * d, cy + Math.sin(a) * d, col, d < len - 2 ? 2 : 1);
  }
  if (t < 0.5) { px(ctx, cx - 2, cy - 2, '#ffffff', 4); }
}

/** Vertical indigo shimmer lines rising along a guarding body. */
function drawShimmer(ctx: CanvasRenderingContext2D, n: number, total: number, w: number, h: number): void {
  const t = n / Math.max(1, total - 1);
  for (let i = 0; i < 6; i++) {
    const x = 8 + i * 6 + (i % 2 ? 2 : 0);
    const y0 = h - 6 - ((t * 40 + i * 9) % (h - 8));
    px(ctx, x, y0, i % 2 ? '#6fd3c8' : '#e2c48c', 1);
    px(ctx, x, y0 + 1, 'rgba(111,211,200,0.6)', 1);
  }
  ctx.fillStyle = 'rgba(111,211,200,0.10)';
  ctx.fillRect(6, 4, w - 12, h - 8);
}

/** Returns the texture key holding this effect (real art or the dev sheet), creating the animation once. */
export function ensureFx(scene: Phaser.Scene, name: FxName): string {
  const spec = SPECS[name];
  let key = spec.texture;
  if (!scene.textures.exists(key)) {
    key = `${spec.texture}--dev`;
    if (!scene.textures.exists(key)) {
      const canvas = scene.textures.createCanvas(key, spec.w * spec.frames, spec.h);
      if (canvas) {
        const ctx = canvas.getContext();
        ctx.imageSmoothingEnabled = false;
        for (let n = 0; n < spec.frames; n++) {
          ctx.save(); ctx.translate(n * spec.w, 0);
          ctx.beginPath(); ctx.rect(0, 0, spec.w, spec.h); ctx.clip();
          spec.draw(ctx, n, spec.frames, spec.w, spec.h);
          ctx.restore();
          canvas.add(n, 0, n * spec.w, 0, spec.w, spec.h);
        }
        canvas.refresh();
      }
    }
  }
  const animKey = fxAnimKey(name);
  if (!scene.anims.exists(animKey)) {
    const tex = scene.textures.get(key);
    const names = tex.getFrameNames().filter((f) => f !== '__BASE');
    const frames: Phaser.Types.Animations.AnimationFrame[] = names.length
      ? names.slice(0, spec.frames).map((f) => ({ key, frame: f }))
      : Array.from({ length: spec.frames }, (_, i) => ({ key, frame: i }));
    if (frames.length) scene.anims.create({ key: animKey, frames, frameRate: spec.fps, repeat: 0 });
  }
  return key;
}

export interface PlayFxOptions { flipX?: boolean; depth?: number; scale?: number; effects?: IEffectsDirector | null; params?: Record<string, unknown> }

/**
 * Plays a one-shot effect at (x, y); resolves when it completes. The shared effects director owns the
 * effect when it implements it (ONE_SHOT_NAMES); otherwise the battle draws its own sprite.
 */
export function playFx(scene: Phaser.Scene, name: FxName, x: number, y: number, opts: PlayFxOptions = {}): Promise<void> {
  if (opts.effects && ONE_SHOT_NAMES.includes(name)) return opts.effects.play(name, { x: Math.round(x), y: Math.round(y), ...(opts.params ?? {}) }).catch(() => undefined);
  const key = ensureFx(scene, name);
  const animKey = fxAnimKey(name);
  if (!scene.anims.exists(animKey)) return Promise.resolve();
  const sprite = scene.add.sprite(Math.round(x), Math.round(y), key).setDepth(opts.depth ?? 500);
  if (opts.flipX) sprite.setFlipX(true);
  if (opts.scale) sprite.setScale(opts.scale);
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => { if (done) return; done = true; if (sprite.active) sprite.destroy(); resolve(); };
    sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, finish);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, finish);
    sprite.play(animKey);
    // Safety net: never hang a battle on a missing animation event.
    scene.time.delayedCall(1200, finish);
  });
}

export interface FloatTextOptions { color?: string; stroke?: string; size?: number; depth?: number; rise?: number; ms?: number }

/** Floating combat number / word (damage, 落空, 虚招…). Integer positions, ends destroyed. */
export function floatText(scene: Phaser.Scene, x: number, y: number, text: string, opts: FloatTextOptions = {}): void {
  const t = battleText(scene, x, y, text, {
    fontFamily: FONT_BODY,
    fontSize: `${opts.size ?? 16}px`,
    fontStyle: 'bold',
    color: opts.color ?? '#fff1c2',
    stroke: opts.stroke ?? '#4a1410',
    strokeThickness: 3,
  }).setOrigin(0.5, 1).setDepth(opts.depth ?? 600);
  const startY = Math.round(y);
  const rise = opts.rise ?? 22;
  const ms = opts.ms ?? 720;
  const proxy = { p: 0 };
  scene.tweens.add({
    targets: proxy, p: 1, duration: ms, ease: 'Cubic.easeOut',
    onUpdate: () => { t.setY(startY - Math.round(proxy.p * rise)); if (proxy.p > 0.6) t.setAlpha(1 - (proxy.p - 0.6) / 0.4); },
    onComplete: () => { if (t.active) t.destroy(); },
  });
}
